/**
 * Retrieve Tags page.
 * (2026-09-28, Jason Cox) Vendor-only. Lets a vendor take their own items back
 * out of a SmartBox so the removal is recorded as a supplier return (a
 * negative-quantity transfer ticket) instead of being billed to the customer
 * as a consumption event (delivery ticket).
 *
 * Flow: scan tags -> each is checked by Smart -> confirm -> tags are flagged
 * -> the vendor physically removes them -> the next SmartBox scan posts the
 * return. See services/smart/retrieveApi.ts for why the flag must come BEFORE
 * the item leaves the box.
 *
 * Visibility is a PWA rule (permissions.canRetrieveTags = vendor account);
 * Smart itself has no dedicated permission for this action.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import Layout from '../components/Layout';
import RfidScannerPanel from '../components/RfidScannerPanel';
import QRScannerModal from '../components/QRScannerModal';
import RetrieveTagsTable, { RetrieveRow } from '../components/RetrieveTagsTable';
import { useAuth, usePermissions } from '../contexts/AuthContext';
import { useUnifiedApi } from '../hooks/useUnifiedApi';
import { CameraIcon, PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';

type Step = 'scan' | 'confirm' | 'done';

/** Wait this long after the last scan before checking, so a burst of R6 reads is one batch. */
const CHECK_DEBOUNCE_MS = 400;

const RetrieveTags: React.FC = () => {
  const { user } = useAuth();
  const permissions = usePermissions();
  const api = useUnifiedApi();
  const location = useLocation();

  const isBlocked = !user || user.system !== 'smart' || !permissions.canRetrieveTags;

  const [rows, setRows] = useState<RetrieveRow[]>([]);
  const [step, setStep] = useState<Step>('scan');
  const [manualEpc, setManualEpc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showQRScannerModal, setShowQRScannerModal] = useState(false);
  const [message, setMessage] = useState<{ kind: 'success' | 'error' | 'info'; text: string } | null>(null);

  // EPCs added since the last check ran. Kept in a ref so the debounced check
  // always sees every tag scanned during the burst, not a stale snapshot.
  const pendingChecks = useRef<Set<string>>(new Set());
  const checkTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (checkTimer.current) clearTimeout(checkTimer.current);
  }, []);

  /** Run the Smart check for every tag queued since the last run. */
  const runPendingChecks = useCallback(async () => {
    const rfids = Array.from(pendingChecks.current);
    pendingChecks.current.clear();
    if (!api?.checkRetrieveTags || rfids.length === 0) return;
    try {
      const results = await api.checkRetrieveTags(rfids);
      setRows((prev) =>
        prev.map((row) => {
          const check = results.find((r) => r.rfid === row.rfid);
          if (!check || row.state !== 'checking') return row;
          return { ...row, check, state: check.eligible ? 'ready' : 'blocked' };
        }),
      );
    } catch (err: any) {
      Sentry.captureException(err, { tags: { feature: 'retrieve-tags', step: 'check' } });
      setRows((prev) =>
        prev.map((row) =>
          rfids.includes(row.rfid) && row.state === 'checking'
            ? { ...row, state: 'blocked', check: { rfid: row.rfid, eligible: false, reason: 'error', message: err?.message } }
            : row,
        ),
      );
    }
  }, [api]);

  const addTag = useCallback((epc: string) => {
    const clean = (epc || '').trim().toUpperCase();
    if (!clean) return;
    setRows((prev) => {
      if (prev.some((r) => r.rfid === clean)) {
        setMessage({ kind: 'info', text: `Tag already scanned: ${clean}` });
        return prev;
      }
      return [...prev, { rfid: clean, state: 'checking' }];
    });
    pendingChecks.current.add(clean);
    if (checkTimer.current) clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(runPendingChecks, CHECK_DEBOUNCE_MS);
  }, [runPendingChecks]);

  // Pre-load a tag handed over from Manage Tags (/retrieve-tags?rfid=EPC).
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || isBlocked || !api) return;
    prefilled.current = true;
    const rfid = new URLSearchParams(location.search).get('rfid');
    if (rfid) addTag(rfid);
  }, [api, isBlocked, location.search, addTag]);

  const removeTag = (rfid: string) => {
    pendingChecks.current.delete(rfid);
    setRows((prev) => prev.filter((r) => r.rfid !== rfid));
  };

  const readyRows = useMemo(() => rows.filter((r) => r.state === 'ready'), [rows]);
  const blockedRows = useMemo(() => rows.filter((r) => r.state === 'blocked'), [rows]);
  const checkingCount = rows.filter((r) => r.state === 'checking').length;
  const totalUnits = readyRows.reduce((sum, r) => sum + (r.check?.packageQty || 0), 0);
  // Reason: every tag in the batch must have passed the check. Letting the
  // vendor submit "the good ones" silently would leave a blocked tag in their
  // hand that they'd assume was handled -- and it would bill as a consumption.
  const canSubmit = readyRows.length > 0 && blockedRows.length === 0 && checkingCount === 0;

  const addManualTag = () => {
    if (manualEpc.trim()) {
      addTag(manualEpc);
      setManualEpc('');
    }
  };

  const resetAll = () => {
    pendingChecks.current.clear();
    setRows([]);
    setStep('scan');
    setMessage(null);
  };

  /**
   * Flag the ready tags, then re-check them to confirm the flag stuck.
   *
   * Reason for the re-check: editAppPDAPreReturn answers "success" even when
   * it updated nothing, so success alone doesn't prove a tag is flagged. A
   * flagged tag re-checks as 'already_flagged', which is the real proof.
   */
  const handleConfirm = async () => {
    if (!api?.flagTagsForRetrieval || !api.checkRetrieveTags) return;
    const rfids = readyRows.map((r) => r.rfid);
    setSubmitting(true);
    setMessage(null);
    try {
      await Sentry.startSpan({ op: 'ui.action', name: 'Retrieve Tags Submit' }, async (span) => {
        span.setAttribute('tagCount', rfids.length);
        try {
          await api.flagTagsForRetrieval!(rfids);
        } catch (err: any) {
          Sentry.captureException(err, { tags: { feature: 'retrieve-tags', step: 'flag' } });
          // Flagging is safe to repeat, so the vendor can simply try again.
          setMessage({ kind: 'error', text: `Retrieve failed: ${err?.message || err}. Nothing was lost. Please try again.` });
          return;
        }

        const verify = await api.checkRetrieveTags!(rfids).catch(() => []);
        const confirmed = new Set(verify.filter((v) => v.reason === 'already_flagged').map((v) => v.rfid));
        span.setAttribute('confirmedCount', confirmed.size);
        setRows((prev) =>
          prev
            .filter((r) => rfids.includes(r.rfid))
            .map((r) => ({ ...r, state: confirmed.has(r.rfid) ? 'flagged' : 'unconfirmed' })),
        );
        setStep('done');
        if (confirmed.size < rfids.length) {
          setMessage({
            kind: 'error',
            text: `${rfids.length - confirmed.size} tag(s) could not be confirmed as flagged. Do not remove those items yet. Tap "Retrieve more tags" and scan them again: "Already flagged" means they are done.`,
          });
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (isBlocked) return <Navigate to="/dashboard" replace />;

  const flaggedCount = rows.filter((r) => r.state === 'flagged').length;

  return (
    <Layout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <div>
            <h2 className="text-2xl font-bold text-secondary-900">Retrieve Tags</h2>
            <p className="mt-1 text-sm text-secondary-600">
              Take your items back out of a SmartBox as a return. The customer is not billed for them.
            </p>
          </div>

          {message && (
            <div
              className={`rounded-md p-3 flex items-start justify-between ${
                message.kind === 'success' ? 'bg-green-50 text-green-800'
                  : message.kind === 'error' ? 'bg-red-50 text-red-800'
                  : 'bg-blue-50 text-blue-800'
              }`}
            >
              <span className="text-sm">{message.text}</span>
              <button onClick={() => setMessage(null)} aria-label="Dismiss message">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          )}

          {step === 'scan' && (
            <>
              <p className="text-xs text-secondary-600">
                Scan each item you are taking out, with the RFID hand scanner or your device camera.
              </p>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <RfidScannerPanel
                  title="Hand Scanner"
                  onTagScanned={addTag}
                  onError={(text) => setMessage({ kind: 'error', text })}
                />
                <div className="card">
                  <div className="card-body">
                    <h3 className="text-sm font-semibold text-secondary-900 mb-2">Camera</h3>
                    <button
                      type="button"
                      onClick={() => setShowQRScannerModal(true)}
                      className="btn-primary w-full flex flex-col items-center justify-center whitespace-nowrap"
                    >
                      <CameraIcon className="h-5 w-5 mb-1" />
                      <span>Start</span>
                      <span>Camera</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={manualEpc}
                  onChange={(e) => setManualEpc(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addManualTag(); }}
                  placeholder="Enter EPC..."
                  maxLength={30}
                  className="input w-44 max-w-full font-mono uppercase tracking-wider"
                />
                <button onClick={addManualTag} className="btn-secondary">
                  <PlusIcon className="h-4 w-4 inline mr-1" /> Add Tag
                </button>
                {rows.length > 0 && (
                  <button onClick={resetAll} className="btn-secondary text-red-600">
                    <TrashIcon className="h-4 w-4 inline mr-1" /> Clear ({rows.length})
                  </button>
                )}
              </div>

              <RetrieveTagsTable rows={rows} onRemove={removeTag} />

              {blockedRows.length > 0 && (
                <div className="rounded-md p-3 bg-red-50 text-red-800 text-sm">
                  {blockedRows.length} tag(s) cannot be retrieved. Remove them from the list (and leave those
                  items in the box) before continuing.
                </div>
              )}

              {rows.length > 0 && (
                <div className="flex justify-end">
                  <button onClick={() => setStep('confirm')} disabled={!canSubmit} className="btn-primary disabled:opacity-50">
                    Retrieve {readyRows.length} tag(s)
                  </button>
                </div>
              )}
            </>
          )}

          {step === 'confirm' && (
            <div className="card">
              <div className="card-body space-y-3">
                <h3 className="text-lg font-semibold text-secondary-900">Confirm retrieval</h3>
                <p className="text-sm text-secondary-700">
                  You are retrieving <strong>{readyRows.length} tag(s)</strong>
                  {totalUnits > 0 && <> ({totalUnits} unit(s))</>}.
                </p>
                <ul className="text-sm text-secondary-700 list-disc pl-5 space-y-1">
                  <li>After you confirm, take these items out of the SmartBox.</li>
                  <li>
                    When the box next scans, each one is recorded as a return to you (a negative
                    transfer ticket), not a customer consumption.
                  </li>
                  <li>The tags go back to Pending and can be restocked or matched again.</li>
                  <li>This app cannot undo a retrieval once it is confirmed.</li>
                </ul>
                <RetrieveTagsTable rows={readyRows} />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setStep('scan')} disabled={submitting} className="btn-secondary">
                    Back
                  </button>
                  <button onClick={handleConfirm} disabled={submitting} className="btn-primary disabled:opacity-50">
                    {submitting ? 'Retrieving...' : 'Confirm retrieval'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="card">
              <div className="card-body space-y-3">
                {flaggedCount > 0 && (
                  <div className="rounded-md p-3 bg-green-50 text-green-800 text-sm">
                    <p className="font-semibold">
                      {flaggedCount} tag(s) flagged. Remove these items from the SmartBox now.
                    </p>
                    <p className="mt-1">
                      The return posts on the box&apos;s next scan. If an item stays in the box, it stays
                      flagged and does not post.
                    </p>
                  </div>
                )}
                <RetrieveTagsTable rows={rows} />
                <div className="flex justify-end">
                  <button onClick={resetAll} className="btn-primary">Retrieve more tags</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <QRScannerModal
        isOpen={showQRScannerModal}
        onClose={() => setShowQRScannerModal(false)}
        onTagsAdded={(tags) => {
          tags.forEach((t) => addTag(t));
          setShowQRScannerModal(false);
        }}
      />
    </Layout>
  );
};

export default RetrieveTags;
